import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  IonPage,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonButton,
  IonIcon,
  IonSpinner,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonItem,
  IonLabel,
  IonTextarea,
  IonInput,
  IonText,
  IonSelect,
  IonSelectOption
} from '@ionic/react';
import {
  checkmarkCircleOutline,
  documentTextOutline,
  closeOutline,
  trashOutline,
  addOutline
} from 'ionicons/icons';
import config from '../config';
import './HMSHome.css';

const ALLOWED_ORIGINS = [
  'https://www.infinity-mc-hms.com',
  'https://infinity-mc-healthplan-edit-dot-infinity-medical-concier-724b0.lm.r.appspot.com',
  'http://localhost:3000',
  'http://localhost:5000',
  'https://localhost:3000',
  'https://localhost:5000'
];

const HMSHome = () => {
  const [auth_token, setAuthToken] = useState(() => localStorage.getItem('auth_token') || '');
  const [user_id, setUserId] = useState(() => localStorage.getItem('user_id') || '');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notesModal, setNotesModal] = useState({ isOpen: false, task: null, notes: [] });
  const [newNote, setNewNote] = useState({ date: new Date().toISOString().split('T')[0], note: '' });
  const [savingNote, setSavingNote] = useState(false);
  const [updatingFollowUp, setUpdatingFollowUp] = useState({});
  const [completingTask, setCompletingTask] = useState({});
  const initialConsultationModalState = {
    isOpen: false,
    task: null,
    encounters: [],
    selectedEncounter: '',
    loading: false,
    error: null
  };
  const [consultationModal, setConsultationModal] = useState(initialConsultationModalState);
  const [memberOverview, setMemberOverview] = useState([]);
  const [loadingMemberOverview, setLoadingMemberOverview] = useState(false);
  const [memberOverviewError, setMemberOverviewError] = useState(null);
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [membersError, setMembersError] = useState(null);
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [showMemberSuggestions, setShowMemberSuggestions] = useState(false);

  // Listen for auth data from parent window
  useEffect(() => {
    const handleMessage = (event) => {
      if (!event?.data) return;

      // Ignore MetaMask and webpack messages
      if (event.data?.target === 'metamask-contentscript' || 
          event.data?.target === 'metamask-inpage' ||
          event.data?.type === 'webpackWarnings') {
        return;
      }

      if (!ALLOWED_ORIGINS.includes(event.origin) && event.origin !== window.origin) {
        return;
      }

      if (event.data && event.data.type === 'AUTH_DATA') {
        const { auth_token: receivedAuthToken, user_id: receivedUserId } = event.data;
        if (receivedAuthToken && receivedUserId) {
          setAuthToken(receivedAuthToken);
          setUserId(receivedUserId);
          localStorage.setItem('auth_token', receivedAuthToken);
          localStorage.setItem('user_id', receivedUserId);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const fetchTasks = useCallback(async () => {
    if (!auth_token || !user_id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${config.apiUrl}/calc_tasks_user/today_and_older/${user_id}`,
        {
          headers: {
            'Authorization': `Bearer ${auth_token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch tasks: ${response.statusText}`);
      }

      const data = await response.json();
      setTasks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError(err.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [auth_token, user_id]);

  const fetchMemberOverview = useCallback(async () => {
    if (!auth_token) return;

    setLoadingMemberOverview(true);
    setMemberOverviewError(null);

    try {
      const response = await fetch(
        `${config.apiUrl}/calc_member_overview_priority`,
        {
          headers: {
            'Authorization': `Bearer ${auth_token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch member overview: ${response.statusText}`);
      }

      const data = await response.json();
      setMemberOverview(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching member overview:', err);
      setMemberOverviewError(err.message || 'Failed to load member overview');
    } finally {
      setLoadingMemberOverview(false);
    }
  }, [auth_token]);

  const fetchMembers = useCallback(async () => {
    if (!auth_token) return;

    setLoadingMembers(true);
    setMembersError(null);

    try {
      const response = await fetch(
        `${config.apiUrl}/members_names`,
        {
          headers: {
            'Authorization': `Bearer ${auth_token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch members: ${response.statusText}`);
      }

      const data = await response.json();
      setMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching members:', err);
      setMembersError(err.message || 'Failed to load members');
    } finally {
      setLoadingMembers(false);
    }
  }, [auth_token]);

  const sendSelectedMemberToParent = useCallback((memberId) => {
    if (!memberId) return;

    const message = {
      type: 'HMS_MEMBER_SELECTED',
      memberId
    };

    try {
      if (window.parent && window.parent !== window) {
        // Using '*' because the parent origins may vary; parent should validate origin on receipt.
        window.parent.postMessage(message, '*');
      }
    } catch (err) {
      console.error('Error posting selected member to parent:', err);
    }
  }, []);

  useEffect(() => {
    if (auth_token) {
      fetchMembers();
    }
  }, [auth_token, fetchMembers]);

  // Ensure member search is cleared on initial load / reload
  useEffect(() => {
    setMemberSearchTerm('');
    setSelectedMemberId('');
    setShowMemberSuggestions(false);
  }, []);

  const filteredMembers = useMemo(() => {
    if (!memberSearchTerm.trim()) return members;

    const search = memberSearchTerm.toLowerCase();

    return members.filter((member) => {
      const first = (member.First_name || '').toLowerCase();
      const last = (member.Surname || '').toLowerCase();
      const full = `${first} ${last}`.trim();

      return (
        first.includes(search) ||
        last.includes(search) ||
        full.includes(search)
      );
    });
  }, [members, memberSearchTerm]);

  // Fetch tasks and member overview when auth data is available
  useEffect(() => {
    if (auth_token && user_id) {
      fetchTasks();
      fetchMemberOverview();
    }
  }, [auth_token, user_id, fetchTasks, fetchMemberOverview]);


  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    } catch {
      return dateString;
    }
  };

  const isOverdue = (dueDate, followUpDate) => {
    if (!dueDate && !followUpDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const checkDate = followUpDate || dueDate;
    if (!checkDate) return false;
    
    const date = new Date(checkDate);
    date.setHours(0, 0, 0, 0);
    
    return date < today;
  };

  const handleFollowUpDateChange = async (task, newDate) => {
    if (!task.salesforce_task_id) {
      console.error('Task missing salesforce_task_id');
      return;
    }

    setUpdatingFollowUp(prev => ({ ...prev, [task.salesforce_task_id]: true }));

    try {
      // Check if new date is in the future
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const newDateObj = new Date(newDate);
      newDateObj.setHours(0, 0, 0, 0);

      // Update follow-up date via API
      // Assuming we can PATCH the task - if not, we may need to use a different endpoint
      const response = await fetch(
        `${config.apiUrl}/salesforce_tasks/sent_to_salesforce`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${auth_token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: task.salesforce_task_id,
            created_at: task.created_at || null,
            category: task.category || null,
            sent_to_salesforce: task.sent_to_salesforce || null,
            completed_in_salesforce: task.completed_in_salesforce || null,
            category_id: task.category_id || null,
            assigned_to_users_id: task.assigned_to_users_id || null,
            task_action_required_id: task.task_action_required_id || null,
            salesforce_encounters_id: task.salesforce_encounters_id || null,
            follow_up_date: newDate,
            completed_by: task.completed_by || null,
            user_id: user_id,
            members_id: task.members_id || null
          })
        }
      );

      if (!response.ok) {
        // If PATCH doesn't work, try alternative endpoint
        const altResponse = await fetch(
          `${config.apiUrl}/salesforce_tasks/sent_to_salesforce`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${auth_token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              salesforce_task_id: task.salesforce_task_id,
              created_at: task.created_at || null,
              category: task.category || null,
              sent_to_salesforce: task.sent_to_salesforce || null,
              completed_in_salesforce: task.completed_in_salesforce || null,
              category_id: task.category_id || null,
              assigned_to_users_id: task.assigned_to_users_id || null,
              task_action_required_id: task.task_action_required_id || null,
              salesforce_encounters_id: task.salesforce_encounters_id || null,
              follow_up_date: newDate,
              completed_by: task.completed_by || null,
              user_id: user_id,
              members_id: task.members_id || null
            })
          }
        );

        if (!altResponse.ok) {
          throw new Error('Failed to update follow-up date');
        }
      }

      // If date moved to future, remove from list; otherwise update it
      if (newDateObj > today) {
        setTasks(prev => prev.filter(t => t.salesforce_task_id !== task.salesforce_task_id));
      } else {
        setTasks(prev =>
          prev.map(t =>
            t.salesforce_task_id === task.salesforce_task_id
              ? { ...t, follow_up_date: newDate }
              : t
          )
        );
      }
    } catch (err) {
      console.error('Error updating follow-up date:', err);
      alert('Failed to update follow-up date. Please try again.');
    } finally {
      setUpdatingFollowUp(prev => {
        const next = { ...prev };
        delete next[task.salesforce_task_id];
        return next;
      });
    }
  };

  const completeSalesforceTask = async (task, { spinnerManaged = false } = {}) => {
    if (!task.salesforce_task_id) {
      console.error('Task missing salesforce_task_id');
      return;
    }

    if (!spinnerManaged) {
      setCompletingTask(prev => ({ ...prev, [task.salesforce_task_id]: true }));
    }

    try {
      const response = await fetch(
        `${config.apiUrl}/salesforce_tasks/complete/${task.salesforce_task_id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${auth_token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            user_id: user_id,
            members_id: task.members_id || null
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to mark task as complete');
      }

      // Remove task from list
      setTasks(prev => prev.filter(t => t.salesforce_task_id !== task.salesforce_task_id));
    } catch (err) {
      console.error('Error marking task complete:', err);
      alert('Failed to mark task as complete. Please try again.');
    } finally {
      if (!spinnerManaged) {
        setCompletingTask(prev => {
          const next = { ...prev };
          delete next[task.salesforce_task_id];
          return next;
        });
      }
    }
  };

  const formatDateForApi = (dateObj) => {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchOpenEncounters = async (membersId) => {
    if (!membersId) {
      return [];
    }

    const response = await fetch(
      `${config.apiUrl}/salesforce_encounters/unmatched_appointments/${membersId}`,
      {
        headers: {
          'Authorization': `Bearer ${auth_token}`,
          'Accept': 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to load encounters');
    }

    const encounters = await response.json();
    return Array.isArray(encounters) ? encounters : [];
  };

  const openConsultationModal = async (task) => {
    setConsultationModal({
      isOpen: true,
      task,
      encounters: [],
      selectedEncounter: '',
      loading: true,
      error: null
    });

    try {
      const encounters = await fetchOpenEncounters(task.members_id);
      setConsultationModal(prev => ({
        ...prev,
        encounters,
        loading: false
      }));
    } catch (err) {
      console.error('Error fetching encounters:', err);
      setConsultationModal(prev => ({
        ...prev,
        loading: false,
        error: err.message || 'Failed to load encounters'
      }));
    }
  };

  const closeConsultationModal = () => {
    setConsultationModal(initialConsultationModalState);
  };

  const completeConsultationAndTask = async () => {
    const modalTask = consultationModal.task;
    if (!modalTask || !modalTask.category_id) {
      closeConsultationModal();
      return;
    }

    setCompletingTask(prev => ({ ...prev, [modalTask.salesforce_task_id]: true }));

    try {
      const response = await fetch(
        `${config.apiUrl}/consultations/complete/${modalTask.category_id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${auth_token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            encounter_id: consultationModal.selectedEncounter || null,
            date_complete: formatDateForApi(new Date()),
            user_id,
            members_id: modalTask.members_id || null
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to complete consultation');
      }

      await completeSalesforceTask(modalTask, { spinnerManaged: true });

      setTasks(prev => prev.filter(t => t.salesforce_task_id !== modalTask.salesforce_task_id));
      closeConsultationModal();
    } catch (err) {
      console.error('Error completing consultation:', err);
      alert(err.message || 'Failed to complete consultation. Please try again.');
    } finally {
      setCompletingTask(prev => {
        const next = { ...prev };
        delete next[modalTask.salesforce_task_id];
        return next;
      });
    }
  };

  const handleMarkComplete = (task) => {
    const isConsultation = (task.category || '').toLowerCase() === 'consultation';
    if (isConsultation && task.category_id) {
      openConsultationModal(task);
      return;
    }

    completeSalesforceTask(task);
  };

  const openNotesModal = async (task) => {
    setNotesModal({ isOpen: true, task, notes: [] });
    setNewNote({ date: new Date().toISOString().split('T')[0], note: '' });

    // Fetch existing notes
    try {
      const response = await fetch(
        `${config.apiUrl}/task_notes/${task.salesforce_task_id}`,
        {
          headers: {
            'Authorization': `Bearer ${auth_token}`,
            'Accept': 'application/json'
          }
        }
      );

      if (response.ok) {
        const notes = await response.json();
        setNotesModal(prev => ({ ...prev, notes: Array.isArray(notes) ? notes : [] }));
      }
    } catch (err) {
      console.error('Error fetching notes:', err);
    }
  };

  const handleSaveNote = async () => {
    if (!newNote.note.trim() || !notesModal.task) return;

    setSavingNote(true);

    try {
      const response = await fetch(`${config.apiUrl}/task_notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${auth_token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          added_by_users_id: user_id,
          note: newNote.note,
          date: newNote.date,
          salesforce_tasks_id: notesModal.task.salesforce_task_id,
          user_id: user_id,
          members_id: notesModal.task.members_id || null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save note');
      }

      const savedNote = await response.json();
      setNotesModal(prev => ({
        ...prev,
        notes: [...prev.notes, savedNote]
      }));

      // Update task's has_notes status
      setTasks(prev =>
        prev.map(t =>
          t.salesforce_task_id === notesModal.task.salesforce_task_id
            ? { ...t, has_notes: true }
            : t
        )
      );

      setNewNote({ date: new Date().toISOString().split('T')[0], note: '' });
    } catch (err) {
      console.error('Error saving note:', err);
      alert('Failed to save note. Please try again.');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!notesModal.task) return;

    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      const url = new URL(`${config.apiUrl}/task_notes/${noteId}`);
      url.searchParams.append('user_id', user_id);
      if (notesModal.task.members_id) {
        url.searchParams.append('members_id', notesModal.task.members_id);
      }

      const response = await fetch(url.toString(), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${auth_token}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete note');
      }

      setNotesModal(prev => ({
        ...prev,
        notes: prev.notes.filter(n => n.id !== noteId)
      }));

      // If no notes left, update has_notes status
      const remainingNotes = notesModal.notes.filter(n => n.id !== noteId);
      if (remainingNotes.length === 0) {
        setTasks(prev =>
          prev.map(t =>
            t.salesforce_task_id === notesModal.task.salesforce_task_id
              ? { ...t, has_notes: false }
              : t
          )
        );
      }
    } catch (err) {
      console.error('Error deleting note:', err);
      alert('Failed to delete note. Please try again.');
    }
  };

  const getMemberName = (task) => {
    if (task.member_name_first && task.member_name_last) {
      return `${task.member_name_last}, ${task.member_name_first}`;
    }
    if (task.member_name_first) return task.member_name_first;
    if (task.member_name_last) return task.member_name_last;
    return 'N/A';
  };

  const getTaskName = (task) => {
    let taskName = '';
    if (task.category && task.task) {
      taskName = `${task.category}: ${task.task}`;
    } else {
      taskName = task.task || task.category || 'N/A';
    }
    // Replace HTML entities
    return taskName.replace(/&amp;/g, '&');
  };

  const getMemberFullName = (member) => {
    const firstName = member.First_name || '';
    const surname = member.Surname || '';
    return `${firstName} ${surname}`.trim() || 'N/A';
  };

  const getUrgencyClass = (urgency) => {
    if (urgency === 'A') return 'member-overview-card-urgency-a';
    if (urgency === 'B') return 'member-overview-card-urgency-b';
    return '';
  };

  const formatSummaryAsBullets = (summary) => {
    if (!summary || !summary.trim()) return null;
    
    // Split by semicolon and filter out empty strings
    const tasks = summary
      .split(';')
      .map(task => task.trim())
      .filter(task => task.length > 0);
    
    if (tasks.length === 0) return null;
    
    return tasks;
  };

  if (!auth_token || !user_id) {
    return (
      <IonPage>
        <IonContent>
          <div className="hms-home-container">
            <div className="auth-pending">
              <p>Waiting for authentication...</p>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonContent>
        <div className="hms-home-container">
          {/* Member selector */}
          <div className="member-selector-section">
            <div className="hms-home-header">
              <h1>Select Member</h1>
              <p>Search by first name, surname, or a combination, then select a member to view.</p>
            </div>
            <IonCard>
              <IonCardContent>
                <IonItem>
                  <IonLabel position="stacked">Search Members</IonLabel>
                  <IonInput
                    className="member-search-input"
                    value={memberSearchTerm}
                    placeholder="Start typing a first name, surname, or both..."
                    onIonInput={(e) => {
                      const value = e.detail.value || '';
                      setMemberSearchTerm(value);
                      setShowMemberSuggestions(!!value);
                    }}
                    onIonFocus={() => {
                      if (memberSearchTerm) {
                        setShowMemberSuggestions(true);
                      }
                    }}
                    onIonBlur={() => {
                      // Small timeout so a click on a suggestion still registers before hiding
                      setTimeout(() => setShowMemberSuggestions(false), 150);
                    }}
                  />
                </IonItem>
                {showMemberSuggestions && !loadingMembers && !membersError && filteredMembers.length > 0 && (
                  <div className="member-suggestions">
                    {filteredMembers.slice(0, 10).map((member) => (
                      <button
                        key={member.id}
                        type="button"
                        className={`member-suggestion-item ${
                          selectedMemberId === member.id ? 'member-suggestion-item-selected' : ''
                        }`}
                        onMouseDown={(e) => {
                          // Prevent input blur from firing before click
                          e.preventDefault();
                          const id = member.id;
                          const fullName = getMemberFullName(member);
                          setSelectedMemberId(id);
                          setMemberSearchTerm(fullName);
                          setShowMemberSuggestions(false);
                          sendSelectedMemberToParent(id);
                        }}
                      >
                        {getMemberFullName(member)}
                      </button>
                    ))}
                  </div>
                )}
                {loadingMembers && (
                  <div className="loading-container">
                    <IonSpinner name="crescent" />
                    <p>Loading members...</p>
                  </div>
                )}
                {membersError && (
                  <IonText color="danger">
                    <p>{membersError}</p>
                  </IonText>
                )}
                {!loadingMembers && !membersError && members.length === 0 && (
                  <IonText color="medium">
                    <p>No members available.</p>
                  </IonText>
                )}
              </IonCardContent>
            </IonCard>
          </div>

          <div className="hms-home-header">
            <h1>Your Daily Tasks</h1>
            <p>All your tasks that are due today or before today.</p>
          </div>

          {loading && (
            <div className="loading-container">
              <IonSpinner name="crescent" />
              <p>Loading tasks...</p>
            </div>
          )}

          {error && (
            <IonCard color="danger">
              <IonCardContent>
                <IonText color="light">
                  <strong>Error:</strong> {error}
                </IonText>
                <IonButton
                  fill="outline"
                  color="light"
                  onClick={fetchTasks}
                  style={{ marginTop: '16px' }}
                >
                  Retry
                </IonButton>
              </IonCardContent>
            </IonCard>
          )}

          {!loading && !error && tasks.length === 0 && (
            <IonCard>
              <IonCardContent>
                <p style={{ textAlign: 'center', padding: '20px' }}>
                  No tasks due today or older.
                </p>
              </IonCardContent>
            </IonCard>
          )}

          {!loading && !error && tasks.length > 0 && (
            <div className="tasks-grid">
              {tasks.map((task) => {
                const overdue = isOverdue(task.due_date, task.follow_up_date);
                const isUpdating = updatingFollowUp[task.salesforce_task_id];
                const isCompleting = completingTask[task.salesforce_task_id];

                return (
                  <IonCard
                    key={task.salesforce_task_id || task.category_id}
                    className={`task-card ${overdue ? 'task-card-overdue' : ''}`}
                  >
                    <IonCardHeader>
                      <IonCardTitle className="task-card-title">
                        {getTaskName(task)}
                      </IonCardTitle>
                      <IonText color="medium" className="task-card-member">
                        {getMemberName(task)}
                      </IonText>
                    </IonCardHeader>
                    <IonCardContent>
                      <div className="task-card-details">
                        <div className="task-card-detail-row">
                          <IonText className="task-card-label">Due Date:</IonText>
                          <IonText className={overdue ? 'date-overdue' : ''}>
                            {formatDate(task.due_date)}
                          </IonText>
                        </div>
                        {task.service_provider && (
                          <div className="task-card-detail-row">
                            <IonText className="task-card-label">Service Provider:</IonText>
                            <IonText>{task.service_provider}</IonText>
                          </div>
                        )}
                        <div className="task-card-detail-row">
                          <IonText className="task-card-label">Follow-up Date:</IonText>
                          <div className="task-card-followup">
                            <input
                              type="date"
                              value={formatDateForInput(task.follow_up_date || task.due_date)}
                              onChange={(e) => handleFollowUpDateChange(task, e.target.value)}
                              disabled={isUpdating}
                              className="follow-up-date-input"
                            />
                            {isUpdating && (
                              <IonSpinner name="crescent" style={{ marginLeft: '8px' }} />
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="task-card-actions">
                        <IonButton
                          fill="outline"
                          size="small"
                          onClick={() => openNotesModal(task)}
                          className={task.has_notes ? 'notes-button-active' : 'notes-button'}
                        >
                          <IonIcon icon={documentTextOutline} slot="start" />
                          Notes
                        </IonButton>
                        <IonButton
                          fill="solid"
                          size="small"
                          color="success"
                          onClick={() => handleMarkComplete(task)}
                          disabled={isCompleting}
                        >
                          {isCompleting ? (
                            <IonSpinner name="crescent" />
                          ) : (
                            <>
                              <IonIcon icon={checkmarkCircleOutline} slot="start" />
                              Complete
                            </>
                          )}
                        </IonButton>
                      </div>
                    </IonCardContent>
                  </IonCard>
                );
              })}
            </div>
          )}

          {/* Member Overview Section */}
          <div className="member-overview-section">
            <div className="hms-home-header">
              <h1>Member Overview</h1>
              <p>Priority members requiring attention</p>
            </div>

            {loadingMemberOverview && (
              <div className="loading-container">
                <IonSpinner name="crescent" />
                <p>Loading member overview...</p>
              </div>
            )}

            {memberOverviewError && (
              <IonCard color="danger">
                <IonCardContent>
                  <IonText color="light">
                    <strong>Error:</strong> {memberOverviewError}
                  </IonText>
                  <IonButton
                    fill="outline"
                    color="light"
                    onClick={fetchMemberOverview}
                    style={{ marginTop: '16px' }}
                  >
                    Retry
                  </IonButton>
                </IonCardContent>
              </IonCard>
            )}

            {!loadingMemberOverview && !memberOverviewError && memberOverview.length === 0 && (
              <IonCard>
                <IonCardContent>
                  <p style={{ textAlign: 'center', padding: '20px' }}>
                    No member overview data available.
                  </p>
                </IonCardContent>
              </IonCard>
            )}

            {!loadingMemberOverview && !memberOverviewError && memberOverview.length > 0 && (
              <div className="member-overview-grid">
                {memberOverview.map((member) => (
                  <IonCard
                    key={member.id}
                    className={`member-overview-card ${getUrgencyClass(member.urgency)}`}
                  >
                    <IonCardHeader>
                      <IonCardTitle className="member-overview-name">
                        {getMemberFullName(member)}
                      </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                      {formatSummaryAsBullets(member.summary) ? (
                        <ul className="member-overview-summary-list">
                          {formatSummaryAsBullets(member.summary).map((task, index) => (
                            <li key={index} className="member-overview-summary-item">
                              {task}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <IonText className="member-overview-summary">
                          No tasks
                        </IonText>
                      )}
                    </IonCardContent>
                  </IonCard>
                ))}
              </div>
            )}
          </div>

          {/* Notes Modal */}
          <IonModal
            isOpen={notesModal.isOpen}
            onDidDismiss={() => setNotesModal({ isOpen: false, task: null, notes: [] })}
          >
            <IonHeader>
              <IonToolbar>
                <IonTitle>
                  Task Notes
                  {notesModal.task && (
                    <span className="modal-subtitle">
                      {' - '}
                      {getTaskName(notesModal.task)}
                    </span>
                  )}
                </IonTitle>
                <IonButtons slot="end">
                  <IonButton onClick={() => setNotesModal({ isOpen: false, task: null, notes: [] })}>
                    <IonIcon icon={closeOutline} />
                  </IonButton>
                </IonButtons>
              </IonToolbar>
            </IonHeader>
            <IonContent className="notes-modal-content">
              <div className="notes-list">
                {notesModal.notes.length === 0 ? (
                  <p className="no-notes">No notes yet. Add your first note below.</p>
                ) : (
                  notesModal.notes.map((note) => (
                    <IonCard key={note.id} className="note-card">
                      <IonCardHeader>
                        <div className="note-header">
                          <IonCardTitle>
                            {formatDate(note.date)}
                          </IonCardTitle>
                          <IonButton
                            fill="clear"
                            size="small"
                            color="danger"
                            onClick={() => handleDeleteNote(note.id)}
                          >
                            <IonIcon icon={trashOutline} />
                          </IonButton>
                        </div>
                        {note.user_full_name && (
                          <IonText color="medium">
                            <small>By: {note.user_full_name}</small>
                          </IonText>
                        )}
                      </IonCardHeader>
                      <IonCardContent>
                        <p>{note.note}</p>
                      </IonCardContent>
                    </IonCard>
                  ))
                )}
              </div>

              <div className="add-note-section">
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle>
                      <IonIcon icon={addOutline} />
                      Add New Note
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <IonItem>
                      <IonLabel position="stacked">Date</IonLabel>
                      <IonInput
                        type="date"
                        value={newNote.date}
                        onIonInput={(e) =>
                          setNewNote({ ...newNote, date: e.detail.value })
                        }
                      />
                    </IonItem>
                    <IonItem>
                      <IonLabel position="stacked">Note</IonLabel>
                      <IonTextarea
                        rows={4}
                        value={newNote.note}
                        onIonInput={(e) =>
                          setNewNote({ ...newNote, note: e.detail.value })
                        }
                        placeholder="Enter your note here..."
                      />
                    </IonItem>
                    <IonButton
                      expand="block"
                      onClick={handleSaveNote}
                      disabled={!newNote.note.trim() || savingNote}
                    >
                      {savingNote ? <IonSpinner name="crescent" /> : 'Save Note'}
                    </IonButton>
                  </IonCardContent>
                </IonCard>
              </div>
            </IonContent>
          </IonModal>

          {/* Consultation Encounter Modal */}
          <IonModal
            isOpen={consultationModal.isOpen}
            onDidDismiss={closeConsultationModal}
          >
            <IonHeader>
              <IonToolbar>
                <IonTitle>Link Encounter</IonTitle>
                <IonButtons slot="end">
                  <IonButton onClick={closeConsultationModal}>
                    <IonIcon icon={closeOutline} />
                  </IonButton>
                </IonButtons>
              </IonToolbar>
            </IonHeader>
            <IonContent className="notes-modal-content">
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle>
                    {consultationModal.task
                      ? `Complete Consultation for ${getTaskName(consultationModal.task)}`
                      : 'Complete Consultation'}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonItem>
                    <IonLabel position="stacked">Related Encounter</IonLabel>
                    <IonSelect
                      value={consultationModal.selectedEncounter}
                      placeholder="Select encounter"
                      onIonChange={(e) =>
                        setConsultationModal(prev => ({ ...prev, selectedEncounter: e.detail.value || '' }))
                      }
                      interfaceOptions={{ header: 'Select Encounter' }}
                    >
                      <IonSelectOption value="">No associated Encounter</IonSelectOption>
                      {consultationModal.encounters.map(encounter => (
                        <IonSelectOption key={encounter.id} value={encounter.id}>
                          {`${encounter.encounter_date || 'Unknown Date'} : ${encounter.doctor_name || 'Unknown Doctor'} (${encounter.reason || 'No reason'})`}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  </IonItem>
                  {consultationModal.loading && (
                    <div className="loading-container">
                      <IonSpinner name="crescent" />
                      <p>Loading encounters...</p>
                    </div>
                  )}
                  {consultationModal.error && (
                    <IonText color="danger">
                      <p>{consultationModal.error}</p>
                    </IonText>
                  )}
                  <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
                    <IonButton
                      expand="block"
                      fill="outline"
                      onClick={closeConsultationModal}
                    >
                      Cancel
                    </IonButton>
                    <IonButton
                      expand="block"
                      onClick={completeConsultationAndTask}
                      disabled={consultationModal.loading || completingTask[consultationModal.task?.salesforce_task_id]}
                    >
                      {completingTask[consultationModal.task?.salesforce_task_id] ? (
                        <IonSpinner name="crescent" />
                      ) : (
                        'Complete'
                      )}
                    </IonButton>
                  </div>
                </IonCardContent>
              </IonCard>
            </IonContent>
          </IonModal>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default HMSHome;

